// src/hooks/useDisasterMode.js
// Automatic Disaster Mode.
//
// Activates when the NWS alert store contains an EXTREME or SEVERE alert
// matching one of the disaster trigger types.
//
// Trigger types (from NWS event codes):
//   - Hurricane Warning / Watch
//   - Tornado Warning
//   - Flash Flood Emergency
//   - Tropical Storm Warning
//   - Storm Surge Warning
//   - Evacuation order (from alert text)
//   - Any EXTREME severity alert
//
// What Disaster Mode changes:
//   1. Swaps the main UI for a simplified big-button layout (via PanicScreen)
//   2. Locks low power mode ON
//   3. Shows a persistent red disaster banner
//   4. Disables non-essential tabs (hotspots, traffic detail, readiness score)
//   5. Pushes the user toward the 5 critical actions only
//
// Manual override: user can dismiss and go back to normal UI.
// The override is stored in sessionStorage — reactivates on next load
// if alerts are still active (session only, not persistent).

import { useState, useEffect, useCallback } from 'react';
import { useAlertStore }                     from '@store/alertStore';
import { useAlerts }                         from '@hooks/useAlerts';

// NWS event strings that trigger disaster mode
const DISASTER_TRIGGERS = new Set([
  'Hurricane Warning',
  'Hurricane Watch',
  'Hurricane Local Statement',
  'Tropical Storm Warning',
  'Tropical Storm Watch',
  'Storm Surge Warning',
  'Storm Surge Watch',
  'Tornado Warning',
  'Tornado Watch',
  'Flash Flood Emergency',
  'Flash Flood Warning',
  'Evacuation Immediate',
  'Shelter In Place Warning',
]);

const DISMISS_KEY = 'eos-disaster-mode-dismissed';

// ── Disaster Load Mode configuration ─────────────────────────────
// Reduces ALL refresh intervals and disables non-essential features
// to preserve bandwidth and battery during active disaster events.
// Applied automatically when disaster mode activates.
export const DISASTER_LOAD_CONFIG = {
  // Refresh multiplier — all intervals * this factor
  refreshMultiplier:    4,       // 30s alerts → 2min, 60s map → 4min
  // Feature flags
  disableSatellite:     true,    // tile-intensive
  disableHotspots:      true,    // non-essential
  disableTrafficLayer:  true,    // non-essential
  disableAnalytics:     true,    // background reads
  // Map constraints
  maxPinsDisaster:      50,      // hard cap during disaster
  minConfidence:        0.50,    // higher bar — only credible reports
  // Nav: only show these tabs
  essentialTabs: new Set(['alerts', 'feed', 'map', 'evacuation', 'radio', 'more']),
};

function isDisasterAlert(alert) {
  // EXTREME severity always triggers
  if (alert.severity === 'Extreme') return true;

  // Specific event types trigger
  const event = alert.event ?? alert.title ?? '';
  for (const trigger of DISASTER_TRIGGERS) {
    if (event.includes(trigger)) return true;
  }

  return false;
}

export function useDisasterMode() {
  const { activeAlerts: nwsAlerts } = useAlerts('zones');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Find the triggering alert
  const triggerAlert = nwsAlerts.find(isDisasterAlert) ?? null;
  const active = !!triggerAlert && !dismissed;

  // Alert summary for the banner
  const alertSummary = triggerAlert
    ? {
        event:    triggerAlert.event ?? triggerAlert.title ?? 'Severe Weather Alert',
        severity: triggerAlert.severity ?? 'EXTREME',
        areas:    triggerAlert.areas ?? triggerAlert.areaDesc ?? '',
      }
    : null;

  // When a new disaster alert fires, auto-expand the offline banner
  // and clear any previous dismiss (new threat = fresh warning)
  useEffect(() => {
    if (triggerAlert && dismissed) {
      // A NEW alert arrived after dismiss — re-activate
      // Only clear dismiss if the event type changed (not just re-fetched same alert)
      // Use the alert ID to track this
      const lastId = sessionStorage.getItem('eos-disaster-last-id');
      if (triggerAlert.id && lastId !== triggerAlert.id) {
        setDismissed(false);
        try { sessionStorage.removeItem(DISMISS_KEY); } catch {}
      }
    }
    if (triggerAlert?.id) {
      try { sessionStorage.setItem('eos-disaster-last-id', triggerAlert.id); } catch {}
    }
  }, [triggerAlert?.id, dismissed]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch {}
  }, []);

  const reactivate = useCallback(() => {
    setDismissed(false);
    try { sessionStorage.removeItem(DISMISS_KEY); } catch {}
  }, []);

  const isEssentialTab = (tabId) => {
    if (!active) return true;
    return DISASTER_LOAD_CONFIG.essentialTabs.has(tabId);
  };

  return {
    active,
    triggerAlert,
    alertSummary,
    dismissed,
    dismiss,
    reactivate,
    isEssentialTab,
    config: DISASTER_LOAD_CONFIG,
  };
}
